import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import VerificationInputModule from "react-verification-input";
import * as SelectPrimitive from "@radix-ui/react-select";
import { IonIcon } from "@ionic/react";
import { ChevronDownIcon } from "lucide-react";
import { cn } from "../../lib/utils";
// import { PhoneInput } from '../ui';
const VerificationInput = VerificationInputModule.default || VerificationInputModule;
const ionicInputVariants = cva('flex items-center justify-between gap-3 border rounded-md', {
    variants: {
        variant: {
            default: 'bg-background dark:bg-background-accent ',
            filled: 'bg-[#F2F4F7] dark:bg-transparent'
        },
        size: {
            xs: 'min-h-8 text-xs',
            sm: 'min-h-9 text-sm',
            default: 'min-h-10 text-sm',
            lg: 'min-h-11 text-base',
            xlg: 'min-h-12 text-base'
        }
    },
    defaultVariants: {
        variant: 'default',
        size: 'default'
    }
});
export const IonicInput = /*#__PURE__*/ React.forwardRef(({ id, type, disabled, options, variant, size, prefix, suffix, label, hint, placeholder, labelClass, hintClass, containerClass, innerContainerClass, className, name, defaultValue, value, onBlur, onChange, ...rest }, ref)=>{
    const renderAdornment = (node)=>{
        if (node === null || node === undefined || node === false) {
            return null;
        }
        if (/*#__PURE__*/ React.isValidElement(node) || typeof node === 'string' || typeof node === 'number') {
            return node;
        }
        if (typeof node === 'object') {
            return /*#__PURE__*/ _jsx(IonIcon, {
                icon: node,
                className: "text-inherit"
            });
        }
        return null;
    };
    const widthRef = useRef(null);
    const [width, setWidth] = useState(null);
    const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? '');
    useEffect(()=>{
        // sync external RHF-controlled value if it changes
        if (value !== undefined) setInternalValue(value);
        //
        if (!widthRef.current) return;
        const observer = new ResizeObserver(([entry])=>{
            setWidth(entry.contentRect.width);
        });
        observer.observe(widthRef.current);
        return ()=>observer.disconnect();
    }, [
        value
    ]);
    const inputId = id || `ionic-input_${Date.now()}`;
    // Separate props for <input>, <textarea>, <select>
    const inputProps = {
        ...rest
    };
    const textareaProps = {
        ...rest
    };
    const selectProps = {
        ...rest
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('flex flex-col', containerClass),
        children: [
            label && /*#__PURE__*/ _jsx("label", {
                htmlFor: inputId,
                className: cn('text-text-dark-medium dark:text-text-light-medium text-sm mb-2', labelClass),
                children: label
            }),
            /* eslint-disable no-unused-vars */ type == 'code' && /*#__PURE__*/ _jsx(VerificationInput, {
                onChange: onChange,
                classNames: {
                    container: 'bg-transparent',
                    character: 'bg-transparent border border-border rounded text-text-dark-high dark:text-text-light-high text-[inherit]'
                },
                onBlur: onBlur,
                ...inputProps
            }),
            type != 'code' && /*#__PURE__*/ _jsxs("div", {
                ref: widthRef,
                className: cn(ionicInputVariants({
                    variant,
                    size
                }), `${disabled ? 'opacity-50 pointer-events-none' : 'opacity-100'}`, type != 'select' && 'px-3', innerContainerClass),
                children: [
                    type != 'select' && renderAdornment(prefix),
                    ![
                        'select',
                        'select-native',
                        'textarea',
                        'phone'
                    ].includes(type || 'text') && /*#__PURE__*/ _jsx("input", {
                        type: type || 'text',
                        ref: ref,
                        id: inputId,
                        disabled: disabled,
                        name: name,
                        defaultValue: defaultValue,
                        value: value,
                        onBlur: onBlur,
                        onChange: onChange,
                        className: cn('placeholder-gray-400 outline-none bg-transparent border-0 text-text-dark-high dark:text-text-light-high ', type === 'checkbox' ? 'w-6 h-6 bg-transparent after:hidden checked:primary' : 'w-5/6 flex-grow flex-shrink', className),
                        placeholder: placeholder,
                        ...inputProps
                    }),
                    type == 'textarea' && /*#__PURE__*/ _jsx("textarea", {
                        rows: 5,
                        ref: ref,
                        id: inputId,
                        disabled: disabled,
                        name: name,
                        defaultValue: defaultValue,
                        value: value,
                        onBlur: onBlur,
                        onChange: onChange,
                        className: cn('my-2 w-5/6 flex-grow flex-shrink placeholder-gray-400 outline-none bg-transparent border-0 text-text-dark-high dark:text-text-light-high ', className),
                        placeholder: placeholder,
                        ...textareaProps
                    }),
                    type == 'select' && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SelectPrimitive.Root, {
                                value: internalValue,
                                onValueChange: (val)=>{
                                    setInternalValue(val);
                                    onChange?.({
                                        target: {
                                            name: name ? name : '',
                                            value: val
                                        }
                                    }); // ✅ RHF expects this shape
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs(SelectPrimitive.Trigger, {
                                        className: cn(// 👇 copy your existing input styling classes here
                                        '!relative flex h-9 w-full items-center gap-3 bg-transparent px-3 py-2 text-sm transition-colors', 'outline-none', 'disabled:cursor-not-allowed disabled:opacity-50', className),
                                        children: [
                                            prefix && prefix,
                                            /*#__PURE__*/ _jsx(SelectPrimitive.Value, {
                                                placeholder: placeholder || 'Select...'
                                            }),
                                            suffix && suffix,
                                            /*#__PURE__*/ _jsx(SelectPrimitive.Icon, {
                                                asChild: true,
                                                children: /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                                    className: "ml-auto size-4 opacity-50"
                                                })
                                            })
                                        ]
                                    }),
                                    !rest.ignorePortal ? /*#__PURE__*/ _jsx(SelectPrimitive.Portal, {
                                        children: /*#__PURE__*/ _jsx(SelectPrimitive.Content, {
                                            position: "popper",
                                            style: {
                                                width: width ? `${width}px` : 'auto'
                                            },
                                            align: "start",
                                            sideOffset: 4,
                                            className: cn(ionicInputVariants({
                                                variant
                                            }), 'z-50 rounded-md border text-popover-foreground max-h-[40vh] overflow-auto'),
                                            children: /*#__PURE__*/ _jsx(SelectPrimitive.Viewport, {
                                                className: "p-1",
                                                children: options?.map((option, i)=>/*#__PURE__*/ _jsx(SelectPrimitive.Item, {
                                                        value: String(option.value),
                                                        style: {
                                                            width: width ? `${width - 10}px` : 'auto'
                                                        },
                                                        className: cn('relative inline-flex mx-auto cursor-default select-none items-start rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none', 'focus:bg-primary focus:text-accent-foreground', 'truncate'),
                                                        children: /*#__PURE__*/ _jsx(SelectPrimitive.ItemText, {
                                                            children: option.label
                                                        })
                                                    }, `opt_${option.value}_${i}`))
                                            })
                                        })
                                    }) : /*#__PURE__*/ _jsx(SelectPrimitive.Content, {
                                        position: "popper",
                                        style: {
                                            width: width ? `${width}px` : 'auto'
                                        },
                                        align: "start",
                                        sideOffset: 4,
                                        className: cn(ionicInputVariants({
                                            variant
                                        }), 'z-50 rounded-md border text-popover-foreground max-h-[40vh] overflow-auto'),
                                        children: /*#__PURE__*/ _jsx(SelectPrimitive.Viewport, {
                                            className: "p-1",
                                            children: options?.map((option, i)=>/*#__PURE__*/ _jsx(SelectPrimitive.Item, {
                                                    value: String(option.value),
                                                    style: {
                                                        width: width ? `${width - 10}px` : 'auto'
                                                    },
                                                    className: cn('relative inline-flex mx-auto cursor-default select-none items-start rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none', 'focus:bg-primary focus:text-accent-foreground', 'truncate'),
                                                    children: /*#__PURE__*/ _jsx(SelectPrimitive.ItemText, {
                                                        children: option.label
                                                    })
                                                }, `opt_${option.value}_${i}`))
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "hidden",
                                name: name,
                                value: internalValue,
                                onChange: ()=>{},
                                onBlur: onBlur
                            })
                        ]
                    }),
                    type == 'select-native' && /*#__PURE__*/ _jsxs("select", {
                        ref: ref,
                        id: inputId,
                        disabled: disabled,
                        onChange: onChange,
                        className: cn('w-5/6 flex-grow flex-shrink placeholder-gray-400 outline-none bg-transparent border-0 text-text-dark-high dark:text-text-light-high text-[inherit]', className),
                        ...selectProps,
                        children: [
                            placeholder && /*#__PURE__*/ _jsx("option", {
                                disabled: true,
                                selected: true,
                                children: placeholder
                            }),
                            options && options.map(({ label, value })=>/*#__PURE__*/ _jsx("option", {
                                    value: value,
                                    children: label
                                }, `opt_${value}`))
                        ]
                    }),
                    type != 'select' && renderAdornment(suffix)
                ]
            }),
            hint && /*#__PURE__*/ _jsx("p", {
                className: cn('text-text-dark-low dark:text-text-light-low text-xs mt-0.5', hintClass),
                children: hint
            })
        ]
    });
});
export default IonicInput;

//# sourceMappingURL=Input.js.map